import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/shallow";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { Box, BoxProps, Button, Tab, Tabs } from "@mui/material";
import { mdiEmoticonSadOutline, mdiGiftOutline } from "@mdi/js";

import AppBar from "components/AppBar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import useBoundStore from "store";
import isActiveCountdown from "utils/isActiveCountdown";
import ConfirmDialog from "components/ConfirmDialog";
import useNotifier from "hooks/useNotifier";
import SecretSantaApi, { GetMyPairWishlistRes, SECRET_SANTA_ACTIONS } from "api/secret-santa";
import EventAppsApi from "api/event-apps";
import PageLoader from "components/PageLoader";
import { hasGroupRole } from "utils/groupRoles";
import GeneratePairs from "./GeneratePairs";
import MyWishlist from "./MyWishlist";
import MyPair from "./MyPair";

interface TabPanelProps extends BoxProps {
  "aria-labelledby": string;
  children?: React.ReactNode;
  id: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...rest } = props;

  return (
    <Box role='tabpanel' p={2} hidden={value !== index} {...rest}>
      {value === index && children}
    </Box>
  );
}

export interface SecretSantaProps {
  onBack?: () => void;
}

export default function SecretSanta(props: SecretSantaProps) {
  const { onBack } = props;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoadingEventApps, setIsLoadingEventApps] = useState(true);
  const [isLoadingPair, setIsLoadingPair] = useState(true);
  const [secretSanta, setSecretSanta] = useState<GetMyPairWishlistRes | null>(null);
  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const selected = useBoundStore((state) => state.selected);
  const userAuthId = useBoundStore((state) => state.user.auth?.id);
  const { events, groups, groupsUsers } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      groups: state.appData.groups,
      groupsUsers: state.appData.groupsUsers,
    }))
  );
  const event = events.find((e) => e.id === selected.eventId);
  const group = groups.find((g) => g.id === selected.groupId);
  const isAdmin =
    Boolean(group?.admins.find((id) => id === userAuthId)) || hasGroupRole(group, userAuthId!, "manage-secret-santa");
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { showNotification } = useNotifier();
  const activeSecretSanta = selected.eventApps?.includes("secret-santa");
  const eventAppsApi = useMemo(() => new EventAppsApi(showNotification), [showNotification]);
  const secretSantaApi = useMemo(() => new SecretSantaApi(showNotification), [showNotification]);

  /**
   * If user navigates back, close confirmation dialog
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setConfirmDialogOpen(false);
    }
  }, [navigationType]);

  /**
   * This is used to make an initial fetch of active apps when user
   * navigates directly to this page using the url.
   */
  useEffect(() => {
    (async () => {
      if (isAppFocused && event && group) {
        if (selected.eventApps === null) {
          setIsLoadingEventApps(true);
          const { data } = await eventAppsApi.getActiveApps(event.id, group.id);

          if (data) {
            selected.set({ eventApps: data.active_apps });
          }
        }
        setIsLoadingEventApps(false);
      }
    })();
  }, [event, group, isAppFocused, selected.eventApps]);

  useEffect(() => {
    (async () => {
      if (activeSecretSanta) {
        if (event?.id && userAuthId && activeSecretSanta) {
          setIsLoadingPair(true);
          const { data } = await secretSantaApi.getMyPairWishlist(userAuthId, event.id);
          setSecretSanta(data);
          setIsLoadingPair(false);
        }
      } else {
        setIsLoadingPair(false);
      }
    })();
  }, [activeSecretSanta, event?.id, userAuthId]);

  function renderAppBar(renderTabs = false): JSX.Element {
    const actions: JSX.Element[] = [];
    if (activeSecretSanta && isAdmin) {
      actions.push(
        <Button color='inherit' key='cancel' onClick={handleOpenConfirmDialog}>
          Cancel
        </Button>
      );
    }

    return (
      <>
        <AppBar actions={actions} title='Secret Santa' onBack={onBack} />
        {renderTabs && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              variant='fullWidth'
              value={selected.secretSantaTab}
              onChange={handleTabChange}
              aria-label='Secret Santa tabs'
            >
              <Tab id='my-pair-tab' aria-controls='my-pair-tabpanel' label='My pair' />
              <Tab id='my-santa-tab' aria-controls='my-santa-tabpanel' label='My wishlist' />
            </Tabs>
          </Box>
        )}
      </>
    );
  }

  function handleTabChange(_event: React.SyntheticEvent, newValue: number) {
    selected.set({ secretSantaTab: newValue });
  }

  function handleOpenConfirmDialog() {
    setConfirmDialogOpen(true);
    navigate(location.pathname);
  }

  function handleCloseConfirmDialog() {
    setConfirmDialogOpen(false);
    navigate(-1);
  }

  async function handleConfirmDelete() {
    if (event && group) {
      const { error } = await secretSantaApi.manageSecretSanta(event.id, group.id, SECRET_SANTA_ACTIONS.CANCEL);
      navigate(-1);
      if (!error) {
        selected.set({
          eventApps: selected.eventApps?.filter((app) => app !== "secret-santa"),
        });
      }
    }
  }

  if (!event || !group) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiEmoticonSadOutline} text='Something went wrong' />
      </PageLayout>
    );
  }

  if (isLoadingEventApps || isLoadingPair) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <PageLoader />
      </PageLayout>
    );
  }

  if (isActiveCountdown(event.vote_before) && !activeSecretSanta) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder
          icon={mdiGiftOutline}
          text='Voting time must end before the Secret Santa pairings can be generated'
        />
      </PageLayout>
    );
  }

  if (!activeSecretSanta) {
    return isAdmin ? (
      <PageLayout appBar={renderAppBar()}>
        <GeneratePairs eventId={event.id} groupId={group.id} />
      </PageLayout>
    ) : (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiGiftOutline} text='Secret Santa can be initiated only by group administrators' />
      </PageLayout>
    );
  }

  if (activeSecretSanta && !secretSanta) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder
          icon={mdiGiftOutline}
          text='You can no longer be part of Secret Santa. Pairs were already generated.'
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      appBar={renderAppBar(true)}
      minusHeight='7rem'
      paddingBottom={false}
      containerSx={{ position: "static" }}
    >
      <TabPanel aria-labelledby='my-pair-tab' id='my-pair-tabpanel' index={0} value={selected.secretSantaTab}>
        <MyPair
          eventId={event.id}
          groupId={group.id}
          isAppFocused={isAppFocused}
          userAuthId={userAuthId}
          users={groupsUsers}
        />
      </TabPanel>
      <TabPanel aria-labelledby='my-santa-tab' id='my-santa-tabpanel' index={1} value={selected.secretSantaTab}>
        <MyWishlist eventId={event.id} groupId={group.id} isAppFocused={isAppFocused} userAuthId={userAuthId} />
      </TabPanel>
      <ConfirmDialog
        body='Are you sure you want to cancel Secret Santa? All generated pairs will be deleted.'
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmDelete}
        confirmLabel='Yes'
        closeLabel='No'
        open={confirmDialogOpen}
        title='Cancel Secret Santa'
      />
    </PageLayout>
  );
}
